import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DicomMetadataStore } from '@ohif/core';
import dicomParser from 'dicom-parser';
import filesToStudies from '../routes/Local/filesToStudies';
import helicalLogo from '../../assets/logo.png';

const LOADED_STUDIES_KEY = 'dicom-loaded-studies';

function addLoadedStudyToStorage(uid: string): void {
  try {
    const raw = localStorage.getItem(LOADED_STUDIES_KEY);
    const list: string[] = raw ? JSON.parse(raw) : [];
    if (!list.includes(uid)) {
      list.push(uid);
      localStorage.setItem(LOADED_STUDIES_KEY, JSON.stringify(list));
    }
  } catch { /* ignore */ }
}

const DICOM_CACHE_NAME = 'dicom-files';
const SESSION_ID_KEY = 'dicom-cache-session-id';
const CACHE_SESSION_MARKER = '__session_marker__';

let cacheSessionChecked = false;

async function ensureCacheSession(): Promise<void> {
  if (cacheSessionChecked) return;
  cacheSessionChecked = true;

  // Generate a session ID unique to this browser launch.
  // sessionStorage is per-tab but window.open copies it, so the first tab
  // that ever loads creates the ID and stores it in both sessionStorage AND localStorage.
  // New tabs opened via window.open inherit the sessionStorage copy → they see the same ID.
  // On browser restart all sessionStorage is gone, so the first tab generates a new ID.
  const storedSessionId = localStorage.getItem(SESSION_ID_KEY);
  let tabSessionId = sessionStorage.getItem(SESSION_ID_KEY);

  if (tabSessionId) {
    // Tab has a session ID (first tab or inherited via window.open)
    if (storedSessionId !== tabSessionId) {
      // Browser was restarted and this is the first tab of the new session
      await caches.delete(DICOM_CACHE_NAME);
      localStorage.setItem(SESSION_ID_KEY, tabSessionId);
    }
  } else if (storedSessionId) {
    // No sessionStorage (new tab that didn't inherit) but localStorage exists
    // → cache is from a running session, trust it and adopt the existing ID
    tabSessionId = storedSessionId;
    sessionStorage.setItem(SESSION_ID_KEY, tabSessionId);
  } else {
    // First ever load — no localStorage, no sessionStorage
    tabSessionId = Date.now().toString(36) + Math.random().toString(36).slice(2);
    sessionStorage.setItem(SESSION_ID_KEY, tabSessionId);
    localStorage.setItem(SESSION_ID_KEY, tabSessionId);
  }
}

async function cachedFetch(url: string): Promise<Response> {
  if (typeof caches === 'undefined') return fetch(url);

  await ensureCacheSession();

  const cache = await caches.open(DICOM_CACHE_NAME);
  const cached = await cache.match(url);
  if (cached) return cached;

  const response = await fetch(url);
  if (response.ok) {
    try {
      await cache.put(url, response.clone());
    } catch {
      // QuotaExceededError — storage full, continue without caching
    }
  }
  return response;
}

async function fetchInBatches<T, R>(
  items: T[],
  maxConcurrent: number,
  fn: (item: T) => Promise<R>
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let nextIndex = 0;

  async function worker() {
    while (nextIndex < items.length) {
      const i = nextIndex++;
      results[i] = await fn(items[i]);
    }
  }

  const workers = Array.from({ length: Math.min(maxConcurrent, items.length) }, () => worker());
  await Promise.all(workers);
  return results;
}

const RemoteLocal: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState('');
  const [statusText, setStatusText] = useState('Loading study...');
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    document.body.style.backgroundColor = 'hsl(var(--background))';
    return () => {
      document.body.style.backgroundColor = '';
    };
  }, []);

  useEffect(() => {
    const patientId = searchParams.get('patientId');
    const folderId = searchParams.get('folderId');
    const studyInstanceUID = searchParams.get('studyInstanceUID');

    if (!folderId || (!patientId && !studyInstanceUID)) {
      setError('Missing required query parameters: folderId and either patientId or studyInstanceUID.');
      return;
    }

    const loadStudy = async () => {
      try {
        // Step 1: Fetch the manifest
        // If patientId is available, always use patient-level manifest (gets ALL studies)
        // so the multi-study tab in the new tab shows everything
        setStatusText('Fetching study manifest...');
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8043';
        let manifestUrl: string;
        if (patientId) {
          manifestUrl = `${backendUrl}/api/v1/dicom/patient/manifest.json?patientId=${patientId}&folderId=${folderId}`;
        } else if (studyInstanceUID) {
          manifestUrl = `${backendUrl}/api/v1/dicom/manifest.json?studyInstanceUID=${studyInstanceUID}&folderId=${folderId}`;
        } else {
          manifestUrl = `${backendUrl}/api/v1/dicom/patient/manifest.json?patientId=${patientId}&folderId=${folderId}`;
        }
        const response = await fetch(manifestUrl);

        if (!response.ok) {
          throw new Error(`Failed to fetch manifest: ${response.status} ${response.statusText}`);
        }

        const manifest = await response.json();

        if (!manifest.studies || manifest.studies.length === 0) {
          throw new Error('No studies found in manifest.');
        }

        // Pre-fetch metadata for all studies by downloading just the first DICOM file of each
        // This lets the study panel show real PatientName, StudyDate, etc. before full load
        const studyMetadata: Record<string, any> = {};
        const metadataPromises = manifest.studies.map(async (s: any) => {
          const firstImg = s.series?.[0]?.Images?.[0];
          const firstUrl = typeof firstImg === 'string' ? firstImg : firstImg?.url;
          if (!firstUrl) return;
          try {
            const res = await cachedFetch(firstUrl);
            if (!res.ok) return;
            const arrayBuffer = await res.arrayBuffer();
            const byteArray = new Uint8Array(arrayBuffer);
            const dataSet = dicomParser.parseDicom(byteArray);

            const rawPatientName = dataSet.string('x00100010') || '';
            const patientName = rawPatientName.replace(/\^/g, ' ').trim();

            studyMetadata[s.StudyInstanceUID] = {
              PatientName: patientName,
              StudyDate: dataSet.string('x00080020') || '',
              StudyDescription: dataSet.string('x00081030') || '',
              Modality: dataSet.string('x00080060') || '',
            };
          } catch (e) {
            console.warn(`Failed to fetch metadata for study ${s.StudyInstanceUID}:`, e);
          }
        });
        await Promise.all(metadataPromises);

        // Store the full manifest globally so the study panel (Tab 2) can access all studies
        (window as any).__remoteLocalManifest = {
          manifest,
          patientId,
          folderId,
          studyMetadata,
        };

        // Step 2: Collect file URLs for the target study (others loaded on demand via Tab 2)
        // If studyInstanceUID is specified (e.g. New Tab), load that specific study; otherwise load the first
        let firstStudy = manifest.studies[0];
        if (studyInstanceUID) {
          const target = manifest.studies.find((s: any) => s.StudyInstanceUID === studyInstanceUID);
          if (target) firstStudy = target;
        }
        const dicomEntries: { url: string; file_id: string }[] = [];
        const nonDicomFiles: { url: string; fileName: string }[] = [];

        if (firstStudy.series) {
          for (const series of firstStudy.series) {
            if (series.Images) {
              for (const img of series.Images) {
                const url = typeof img === 'string' ? img : img.url;
                const fileId = typeof img === 'string' ? '' : String(img.file_id || '');
                dicomEntries.push({ url, file_id: fileId });
              }
            }
          }
        }

        if (firstStudy.nonDicomInstances) {
          nonDicomFiles.push(...firstStudy.nonDicomInstances);
        }

        const totalFiles = dicomEntries.length + nonDicomFiles.length;
        setStatusText(`Downloading ${totalFiles} files...`);

        // Step 3: Download all DICOM files and convert to File objects
        // Track file_id per SOPInstanceUID so we can inject into DicomMetadataStore after parsing
        const fileIdMap: Record<string, string> = {};
        let downloaded = 0;
        const updateProgress = () => {
          downloaded++;
          const pct = Math.round((downloaded / totalFiles) * 90);
          setProgress(pct);
          setStatusText(`Downloaded ${downloaded} of ${totalFiles} files...`);
        };

        const dicomFiles = await fetchInBatches(dicomEntries, 6, async ({ url, file_id }) => {
          const res = await cachedFetch(url);
          if (!res.ok) {
            console.warn(`Failed to fetch DICOM file: ${url}`);
            return null;
          }
          const arrayBuffer = await res.arrayBuffer();
          updateProgress();
          // Extract real file name from proxy URL's path param
          let fileName = 'dicom_file';
          try {
            const parsed = new URL(url);
            const filePath = parsed.searchParams.get('path');
            if (filePath) {
              fileName = filePath.split('/').pop() || fileName;
            }
          } catch { /* use default */ }
          // Parse SOPInstanceUID from the downloaded DICOM to map file_id
          if (file_id) {
            try {
              const ds = dicomParser.parseDicom(new Uint8Array(arrayBuffer));
              const sopUID = ds.string('x00080018');
              if (sopUID) {
                fileIdMap[sopUID.trim()] = file_id;
              }
            } catch { /* skip */ }
          }
          return new File([arrayBuffer], fileName, { type: 'application/dicom' });
        });

        // Step 4: Download all non-DICOM files
        const nonDicomResults = await fetchInBatches(nonDicomFiles, 6, async ({ url, fileName }) => {
          const res = await cachedFetch(url);
          if (!res.ok) {
            console.warn(`Failed to fetch non-DICOM file: ${url}`);
            return null;
          }
          const arrayBuffer = await res.arrayBuffer();
          updateProgress();

          // Determine MIME type from file extension
          let mimeType = 'application/octet-stream';
          const ext = fileName.split('.').pop()?.toLowerCase();
          if (ext === 'pdf') {
            mimeType = 'application/pdf';
          } else if (ext === 'txt') {
            mimeType = 'text/plain';
          }

          return new File([arrayBuffer], fileName, { type: mimeType });
        });

        // Step 5: Combine results
        const allFiles = [...dicomFiles, ...nonDicomResults].filter((f): f is File => f !== null);

        if (allFiles.length === 0) {
          throw new Error('No files could be downloaded from the manifest.');
        }

        // Step 6: Feed into the localbasic pipeline
        setProgress(92);
        setStatusText(`Processing ${allFiles.length} files...`);
        const studies = await filesToStudies(allFiles);

        // Inject file_id into DicomMetadataStore instances so deidentification can find them
        if (Object.keys(fileIdMap).length > 0) {
          const studyUIDs = DicomMetadataStore.getStudyInstanceUIDs();
          for (const uid of studyUIDs) {
            const study = DicomMetadataStore.getStudy(uid);
            if (!study) continue;
            for (const ser of study.series || []) {
              for (const inst of ser.instances || []) {
                const sopUID = inst.SOPInstanceUID;
                if (sopUID && fileIdMap[sopUID]) {
                  inst.file_id = fileIdMap[sopUID];
                }
              }
            }
          }
        }

        setProgress(100);

        if (!studies || studies.length === 0) {
          throw new Error('No valid DICOM studies found in downloaded files.');
        }

        // Track this study in localStorage so new tabs can auto-load it
        addLoadedStudyToStorage(firstStudy.StudyInstanceUID);

        // Step 7: Navigate to segmentation mode with dicomlocal
        // Save params to sessionStorage so page refresh can redirect back here
        sessionStorage.setItem('remotelocal_patientId', patientId || '');
        sessionStorage.setItem('remotelocal_folderId', folderId || '');

        const query = new URLSearchParams();
        studies.forEach(id => query.append('StudyInstanceUIDs', id));
        query.append('datasources', 'dicomlocal');

        navigate(`/segmentation/dicomlocal?${decodeURIComponent(query.toString())}`, {
          replace: true,
        });
      } catch (err) {
        console.error('RemoteLocal error:', err);
        setError(err instanceof Error ? err.message : 'An unknown error occurred.');
      }
    };

    loadStudy();
  }, [searchParams, navigate]);

  if (error) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-background">
        <div className="text-center">
          <h2 className="mb-2 text-xl font-semibold text-red-400">Error</h2>
          <p className="text-gray-300">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        backgroundColor: 'hsl(var(--background))',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <img
        src={helicalLogo}
        alt="Helical"
        style={{ height: '80px', marginBottom: '40px', filter: 'brightness(0) invert(1)', opacity: 0.95 }}
      />
      <div
        style={{
          width: '280px',
          height: '6px',
          backgroundColor: 'hsl(var(--card))',
          borderRadius: '3px',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${progress}%`,
            background: 'linear-gradient(90deg, hsl(var(--primary)), hsl(var(--secondary)))',
            borderRadius: '3px',
            transition: 'width 0.3s ease',
          }}
        />
      </div>
      <div
        style={{
          marginTop: '12px',
          fontSize: '14px',
          fontWeight: 600,
          color: 'hsl(var(--foreground))',
          fontFamily: 'system-ui, sans-serif',
        }}
      >
        {progress}%
      </div>
      <div
        style={{
          marginTop: '6px',
          fontSize: '12px',
          color: 'hsl(var(--muted-foreground))',
          fontFamily: 'system-ui, sans-serif',
        }}
      >
        {statusText}
      </div>
    </div>
  );
};

export default RemoteLocal;
